import React from 'react';
import { motion } from 'framer-motion';
import { IMAGES } from '@/assets/images';
import { qualifications } from '@/data/index';
import { QualificationCard } from '@/components/Cards';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { GraduationCap, Award, BookOpen, Heart, Briefcase, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function About() {
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden rounded-sm">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={springPresets.gentle} className="relative z-10">

              <p className="text-xl md:text-2xl text-muted-foreground font-light leading-relaxed mb-8 text-center">
                <em>"Prawdziwa transformacja nie polega na zmianie tego, kim jesteś, lecz na odważnym odkryciu tego, kim zawsze byłaś/byłeś."</em><br/><br/>
                Psycholog, seksuolog, mediator sądowy i biznesowy oraz psychoterapeuta poznawczo-behawioralny w trakcie certyfikacji. Wykładowczyni akademicka i trener, mentor. Jako doktorantka w Instytucie Psychologii Polskiej Akademii Nauk i autorka dwóch nadchodzących publikacji — o procesie wychodzenia z depresji oraz o budowaniu relacji z osobowością narcystyczną — łączę głęboką wiedzę naukovą z praktycznym doświadczeniem terapeutycznym. Wspieram ludzi w sposób pełny, skuteczny i oparty na dowodach naukowych.
              </p>
              <div className="flex flex-wrap gap-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm font-medium">
                  <GraduationCap className="w-4 h-4" /> PhD Candidate
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm font-medium">
                  <Award className="w-4 h-4" /> MBA
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <Heart className="w-4 h-4" /> Psychoterapeuta CBT
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <Users className="w-4 h-4" /> Psycholog
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <Heart className="w-4 h-4" /> Seksuolog
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <Briefcase className="w-4 h-4" /> Mediator
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <BookOpen className="w-4 h-4" /> Trener
                </span>
              </div>
              <div className="mt-8 text-center">
                <Button asChild size="lg" className="rounded-full px-8">
                  <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                    Umów wizytę <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={springPresets.smooth} className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl relative z-10">
                <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2026750872040820736_WhatsApp Image 2026-01-31 at 21.29.01.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Rachela Nowak" className="w-full object-center h-[750px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-accent/20 rounded-full blur-3xl -z-0" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-primary/10 rounded-full blur-2xl -z-0" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Biography Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div variants={fadeInUp} initial="initial" whileInView="animate" viewport={{
            once: true
          }} className="prose prose-lg prose-slate max-w-none text-justify">
              <h2 className="font-serif text-3xl text-primary mb-8">Moja Droga i Filozofia</h2>
              <p className="text-foreground/80 mb-6">
                Moja droga zawodowa to synteza trzech światów: głębokiej wiedzy psychologicznej, precyzji prawniczej i strategicznego myślenia biznesowego. Jako doktorantka w Instytucie Psychologii Polskiej Akademii Nauk, absolwentka MBA oraz studentka prawa, buduję mosty między teorią a praktyką, między nauką a życiem.
              </p>
              <p className="text-foreground/80 mb-6">
                W centrum mojej filozofii leży przekonanie, że autentyczna zmiana wymaga odwagi — odwagi do konfrontacji z prawdami o sobie, odwagi do budowania granic i odwagi do żądania więcej od życia. Kieruję się wartościami integralnej etyki: szacunkiem dla ludzkiej godności, odpowiedzialnością za słowo i czyn oraz przekonaniem, że każdy człowiek zasługuje na przestrzeń do wzrostu.
              </p>
              <p className="text-foreground/80 mb-6">
                Jako prezeska Wellpro Institute oraz Fundacji wspierającej kobiety doświadczające przemocy seksualnej, działam na styku indywidualnej terapii i systemowej zmiany społecznej. Wierzę, że prawdziwa transformacja zachodzi jednocześnie na poziomie osobistym i strukturalnym — nie można leczyć ran powstałych w toksycznych systemach, nie zmieniając samych systemów.
              </p>
              <p className="text-foreground/80">
                Prywatnie jestem mamą dwóch córek, co codziennie przypomina mi o odpowiedzialności za świat, który pozostawiamy następnym pokoleniom. To właśnie ta perspektywa nadaje głęboki sens mojej pracy — każda sesja terapeutyczna, każda mediacja, każde szkolenie to inwestycja w bardziej świadome, empatyczne i sprawiedliwe społeczeństwo.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-primary mb-4">Wykształcenie i Certyfikaty</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Nieustanny rozwój kompetencji pozwala mi na interdyscyplinarne podejście do każdego przypadku.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualifications.map(q => <motion.div key={q.id} variants={staggerItem}>
                <QualificationCard qualification={q} />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Values / Mission Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2026736603723382784_trenerka_coaching_optimized_1.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Gabinet psychologa" className="rounded-2xl shadow-xl aspect-video lg:aspect-square h-[592px] object-cover" />
              <div className="absolute -bottom-8 -left-8 bg-primary text-primary-foreground p-8 rounded-2xl max-w-xs hidden md:block">
                <p className="font-serif italic text-xl">
                  "Łączę światy psychologii, seksuologii, prawa i biznesu, by wspierać Cię w sposób pełny."
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="font-serif text-3xl text-primary">W czym mogę Ci pomóc?</h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center text-accent">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Relacje i Komunikacja</h4>
                    <p className="text-muted-foreground">Naprawa relacji, mediacje rodzinne i biznesowe, budowanie zdrowych granic.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Odporność Psychiczna</h4>
                    <p className="text-muted-foreground">Wsparcie w kryzysach, radzenie sobie ze stresem i wypaleniem zawodowym.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center text-accent">
                    <Heart className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Zdrowie Seksualne</h4>
                    <p className="text-muted-foreground">Konsultacje seksuologiczne, wsparcie w obszarze tożsamości i intymności.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                    <Briefcase className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Rozwój Biznesu</h4>
                    <p className="text-muted-foreground">Mentoring dla liderów, doradztwo strategiczne i wsparcie w procesach zmian organizacyjnych.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center text-accent">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Mediacje Sądowe i Biznesowe</h4>
                    <p className="text-muted-foreground">Profesjonalne rozwiązywanie sporów, mediacje rodzinne i gospodarcze z wpis na listę mediatorów sądowych.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-6">Zrób pierwszy krok w stronę zmiany</h2>
          <p className="text-primary-foreground/80 mb-10 max-w-xl mx-auto">
            Zapraszam na konsultację, podczas której wspólnie określimy cele naszej współpracy i wybierzemy najlepszą drogę wsparcia.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:opacity-90 transition-opacity">
              Umów wizytę
            </button>
            <button className="px-8 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg font-medium transition-colors">
              Kontakt
            </button>
          </div>
        </div>
      </section>
    </div>;
}